import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { useTranslation } from "react-i18next";
import { PAYPAL_CLIENT_ID } from "../../../config/paypal";
import { getLineTotal } from "../PayPalCheckout/UseCart";
import { Edition, Duration, daysForDuration } from "../../Settings";
import { DEBUG } from "../..";
import classes from "./Manager.module.css";

// The license whose edition is being switched. Only LMU TV keys reach here.
export interface EditionTarget {
  key_ident: string;
  edition: Edition; // current
  type: "lifetime" | "subscription";
  seats: number;
  subscription_id?: string;
  duration?: Duration;
  next_billing_date?: string | null;
}

const label = (e: Edition) => (e === "pro" ? "LMU TV Pro" : "LMU TV Standard");

// One-time charge to switch Standard→Pro. Lifetime = full price difference; subscription =
// difference prorated over the days left in the current period.
function upgradePrice(target: EditionTarget): number {
  const d: Duration = target.type === "lifetime" ? "lifetime" : (target.duration ?? "1y");
  const delta = Math.max(0,
    getLineTotal({ edition: "pro", duration: d, pcs: target.seats })
    - getLineTotal({ edition: "standard", duration: d, pcs: target.seats }));
  if (target.type === "lifetime") return delta;
  const periodDays = target.duration ? daysForDuration(target.duration) ?? 0 : 0;
  let factor = 1;
  if (periodDays > 0 && target.next_billing_date) {
    const msLeft = new Date(target.next_billing_date + "T00:00:00").getTime() - Date.now();
    const daysLeft = Math.max(1, Math.min(periodDays, Math.ceil(msLeft / 86400000)));
    factor = daysLeft / periodDays;
  }
  return delta * factor;
}

function PayUpgrade({ amount, onCaptured, onError }: {
  amount: number; onCaptured: (orderId: string) => void; onError: () => void;
}) {
  const [{ isPending }] = usePayPalScriptReducer();
  if (isPending) return <Center py="sm"><Loader color="lmuTVColors" /></Center>;
  return (
    <div style={{ backgroundColor: "#242424" }} className={classes.managerContainer}>
      <PayPalButtons
        forceReRender={[amount]}
        style={{ layout: "vertical", color: "black", shape: "rect" }}
        createOrder={(_d, actions) =>
          actions.order.create({
            intent: "CAPTURE",
            purchase_units: [{ amount: { value: amount.toFixed(2), currency_code: "EUR" } }],
          })
        }
        onApprove={async (_d, actions) => {
          try {
            const details = await actions.order!.capture();
            onCaptured(details.id!);
          } catch (e) { if (DEBUG) console.error(e); onError(); }
        }}
        onError={() => onError()}
      />
    </div>
  );
}

type Phase = "idle" | "busy" | "error" | "success";

export function EditionManager({ target, opened, onClose, onChanged }: {
  target: EditionTarget | null;
  opened: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation("translation");
  const [phase, setPhase] = useState<Phase>("idle");

  if (!target) return null;
  const toEdition: Edition = target.edition === "pro" ? "standard" : "pro";
  const upgrading = toEdition === "pro"; // Standard → Pro
  const price = upgrading ? Number(upgradePrice(target).toFixed(2)) : 0;
  const needsPayment = upgrading && price > 0;

  // New recurring price from the next cycle (subscriptions only).
  const newRecurring = target.type === "subscription"
    ? getLineTotal({ edition: toEdition, duration: (target.duration ?? "1y"), pcs: target.seats })
    : 0;

  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();

  const apply = async (orderId?: string) => {
    setPhase("busy");
    try {
      const fd = new FormData();
      fd.append("edition", toEdition);
      if (target.type === "lifetime") fd.append("key_ident", target.key_ident);
      else fd.append("subscription_id", target.subscription_id ?? "");
      if (orderId) fd.append("order_id", orderId);
      const r = await fetch("/dyn/change-edition.php", { method: "POST", body: fd, credentials: "include" });
      const d = await r.json();
      if (!d.success) { setPhase("error"); return; }
      if (d.approve) { window.location.href = d.approve; return; }
      setPhase("success");
      onChanged();
    } catch (e) { if (DEBUG) console.error(e); setPhase("error"); }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => { if (phase !== "busy") { setPhase("idle"); onClose(); } }}
      title={<Title order={4}>{t("account.edition.title")}</Title>}
      size="md"
      centered
    >
      {phase === "success" ? (
        <Alert color="green" radius="md" title={t("account.edition.successTitle")}>
          <Text size="sm" mb="sm">{t("account.edition.successMsg")}</Text>
          <Button color="lmuTVColors" fullWidth onClick={() => { setPhase("idle"); onClose(); }}>
            {t("menu.user.account")}
          </Button>
        </Alert>
      ) : (
        <Stack gap="md">
          <Group justify="center" gap="sm" align="center">
            <Badge size="lg" variant="light" color="gray">{label(target.edition)}</Badge>
            <IconArrowRight size={18} />
            <Badge size="lg" variant="light" color="lmuTVColors">{label(toEdition)}</Badge>
          </Group>

          <Alert color={upgrading ? "lmuTVColors" : "yellow"} radius="md" variant="light">
            <Text size="sm">
              {upgrading
                ? (target.type === "lifetime" ? t("account.edition.lifetimeUpgradeNote") : t("account.edition.subUpgradeNote"))
                : t("account.edition.subDowngradeNote") + (target.next_billing_date ? ` (${fmtDate(target.next_billing_date)})` : "")}
            </Text>
            {needsPayment && <Text fw={700} mt={4}>{t("account.edition.dueNow")}: {price.toFixed(2)}€</Text>}
            {target.type === "subscription" && (
              <Text size="sm" mt={2}>
                {t("account.edition.newRecurring")}: <b>{newRecurring.toFixed(2)}€</b>
                {target.duration ? ` / ${t(`subscribe.interval.${target.duration}`)}` : ""}
              </Text>
            )}
          </Alert>

          {phase === "error" && (
            <Alert color="red" radius="md" withCloseButton onClose={() => setPhase("idle")}>
              {t("account.edition.errorMsg")}
            </Alert>
          )}

          {needsPayment ? (
            <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR", intent: "capture", disableFunding: "credit" }}>
              <PayUpgrade amount={price} onCaptured={(oid) => apply(oid)} onError={() => setPhase("error")} />
            </PayPalScriptProvider>
          ) : (
            <Button color="lmuTVColors" fullWidth loading={phase === "busy"} onClick={() => apply()}>
              {t("account.edition.apply")}
            </Button>
          )}
        </Stack>
      )}
    </Modal>
  );
}
